import { apiRequest } from './authService';

/**
 * Kassa (Register) service.
 * A daily cash register: each day has an opening balance per currency
 * (USD / SRD / EUR) that resets every day, plus transactions that add
 * ('recebido') or subtract ('pagamento') from the day's saldo.
 */

/**
 * Get everything for one day: opening balances (null if not set yet),
 * transactions, and the computed saldo per currency.
 * @param {string} date YYYY-MM-DD
 */
export async function getDay(date) {
  try {
    const response = await apiRequest('kassa/day', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to load day' };
  } catch (e) {
    console.error('kassa getDay error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Lightweight change signal for a day (for polling-based live sync).
 * @param {string} date
 */
export async function getDayVersion(date) {
  try {
    const response = await apiRequest('kassa/day-version', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data };
    }
    return { success: false };
  } catch (e) {
    // Silent — polling failures shouldn't spam the console/UI.
    return { success: false };
  }
}

/**
 * Set (or update) the opening balances for a day.
 * @param {string} date
 * @param {{usd:number, srd:number, eur:number}} opening
 */
export async function setOpening(date, { usd, srd, eur }) {
  try {
    const response = await apiRequest('kassa/set-opening', {
      method: 'POST',
      body: JSON.stringify({ date, usd, srd, eur })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to save opening balances' };
  } catch (e) {
    console.error('kassa setOpening error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Add a transaction to a day.
 * @param {string} date
 * @param {'pagamento'|'recebido'} type
 * @param {string} description
 * @param {{usd:number, srd:number, eur:number}} amounts
 */
export async function addTransaction(date, type, description, { usd, srd, eur }) {
  try {
    const response = await apiRequest('kassa/add-transaction', {
      method: 'POST',
      body: JSON.stringify({
        date, type, description,
        amount_usd: usd, amount_srd: srd, amount_eur: eur
      })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to add transaction' };
  } catch (e) {
    console.error('kassa addTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Update an existing transaction.
 * @param {number|string} id
 * @param {{type?, description?, usd?, srd?, eur?}} fields
 */
export async function updateTransaction(id, fields) {
  try {
    const body = { id };
    if (fields.type !== undefined) body.type = fields.type;
    if (fields.description !== undefined) body.description = fields.description;
    if (fields.usd !== undefined) body.amount_usd = fields.usd;
    if (fields.srd !== undefined) body.amount_srd = fields.srd;
    if (fields.eur !== undefined) body.amount_eur = fields.eur;

    const response = await apiRequest('kassa/update-transaction', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to update transaction' };
  } catch (e) {
    console.error('kassa updateTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Get the day's closing (saved bill counts if any) + the current live saldo
 * and the denomination list.
 * @param {string} date
 */
export async function getClosing(date) {
  try {
    const response = await apiRequest('kassa/get-closing', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to load closing' };
  } catch (e) {
    console.error('kassa getClosing error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Save (upsert) the day's closing count.
 * @param {string} date
 * @param {{usd:object, srd:object, eur:object}} counts  per-currency {denom: count}
 */
export async function saveClosing(date, counts) {
  try {
    const response = await apiRequest('kassa/save-closing', {
      method: 'POST',
      body: JSON.stringify({ date, counts })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to save closing' };
  } catch (e) {
    console.error('kassa saveClosing error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/** Reopen (unlock) a closed day so it can be edited again. */
export async function reopenClosing(date) {
  try {
    const response = await apiRequest('kassa/reopen-closing', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to reopen' };
  } catch (e) {
    console.error('kassa reopenClosing error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

export async function deleteTransaction(id) {
  try {
    const response = await apiRequest('kassa/delete-transaction', {
      method: 'POST',
      body: JSON.stringify({ id })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to delete transaction' };
  } catch (e) {
    console.error('kassa deleteTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}
